import {
  BMComponentsType,
  BMPrimitives,
  useBMTheme,
} from "@traveloka/district-bloom";
import {
  ComponentProps,
  Fragment,
  useCallback,
  useMemo,
  useState,
} from "react";
import { FlatList, StyleSheet } from "react-native";

import BMView from "../../../../components/View/BMView";
import { getRefundableStatus } from "../../Flight/FlightRefundInfos";
import {
  FlightSummary,
  FlightSummaryBaseComponentProps,
  FlightSummaryDetails,
} from "../../Flight/FlightSummary";
import { useFlightSummaryDetails } from "./hooks";
import { RefundInfoApiData } from "./type";

type Props = {
  summaries: Array<FlightSummaryBaseComponentProps>;
  refundInfo: RefundInfoApiData;
};

export default function FlightPrebookProductSummaries({
  summaries,
  refundInfo,
}: Props) {
  const [showByIndex, setShowByIndex] = useState<number | null>(null);

  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const details = useFlightSummaryDetails(showByIndex, summaries, refundInfo);

  const refundableByIndex = useMemo(() => {
    const list: ComponentProps<typeof FlightSummary>["refundable"][] =
      summaries.map((_, i) => {
        const { data, ...rest } = refundInfo;
        const status = data ? getRefundableStatus(data[i]) : undefined;
        if ((!data || refundInfo.isLoading) && i < summaries.length - 1) {
          return undefined;
        }
        return {
          status,
          ...rest,
        };
      });
    return list;
  }, [summaries, refundInfo]);

  const keyExtractor = useCallback(
    (item: FlightSummaryBaseComponentProps, index: number) => {
      const from = item.summary.departureDetail.airport.city;
      const to = item.summary.arrivalDetail.airport.city;
      const date = item.summary.departureDetail.departureDate;
      return `${item.type}-${from}-${to}-${date}-${index}`;
    },
    []
  );

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: FlightSummaryBaseComponentProps;
      index: number;
    }) => (
      <FlightSummary
        {...item}
        refundable={refundableByIndex[index]}
        onClick={() => setShowByIndex(index)}
      />
    ),
    [refundableByIndex]
  );

  const handleCloseDetails = useCallback(() => setShowByIndex(null), []);

  return (
    <Fragment>
      <BMView style={styles.card}>
        <FlatList
          data={summaries}
          scrollEnabled={false}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ItemSeparatorComponent={() => <BMView style={styles.divider} />}
        />
      </BMView>
      <FlightSummaryDetails
        {...details}
        visible={showByIndex !== null}
        onClose={handleCloseDetails}
      />
    </Fragment>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    divider: {
      borderTopWidth: BMPrimitives.BMBorder.width.thick,
      borderTopColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    card: {
      borderRadius: BMPrimitives.BMRadius.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      overflow: "hidden",
    },
  });
}
